import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@shopify/polaris";
import clsx from "clsx";
import { FunnelConfig, FunnelStepStatus, SaveStatus } from "./types";
import FunnelSidebar from "./FunnelSidebar";
import FunnelStepRenderer from "./FunnelStepRenderer";
import { validateStepFields } from "./validation";
import useIsTouchScreen from "./use-is-touch-screen";

interface FormFunnelProps {
  config: FunnelConfig;
}

const FormFunnel: React.FC<FormFunnelProps> = ({ config }) => {
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "en") as "lt" | "en";
  const isTouch = useIsTouchScreen();
  const { steps, autoSave = true, autoSaveDebounceMs = 2000 } = config;

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [data, setData] = useState<Record<string, any>>(
    config.initialData || {}
  );
  // `data` as of the last change, for the autosave payload (see handleFieldChange).
  const latestData = useRef(data);
  latestData.current = data;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  // onSubmit throws an Error whose message is a text key; `values`, when the
  // Error carries them, fill that text's placeholders.
  const [submitError, setSubmitError] = useState<{
    key: string;
    values?: Record<string, string>;
  } | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The save the autosave timer will send, so it can be sent early (flushed).
  const pendingAutoSave = useRef<{ stepId: string; data: Record<string, any> } | null>(null);
  // After the final submit nothing is autosaved any more (the server would
  // refuse a submitted form).
  const submittedRef = useRef(false);
  const actionLockRef = useRef(false);
  const onSaveRef = useRef(config.onSave);
  onSaveRef.current = config.onSave;
  const onSubmitRef = useRef(config.onSubmit);
  onSubmitRef.current = config.onSubmit;
  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const isSingleStep = steps.length === 1;

  const cancelPendingAutoSave = () => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = null;
    pendingAutoSave.current = null;
  };

  // Auto-save on field change
  const triggerAutoSave = useCallback(
    (stepId: string, newData: Record<string, any>) => {
      if (!autoSave || submittedRef.current) return;
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      pendingAutoSave.current = { stepId, data: newData };
      autoSaveTimer.current = setTimeout(async () => {
        autoSaveTimer.current = null;
        pendingAutoSave.current = null;
        try {
          setSaveStatus("saving");
          await onSaveRef.current(stepId, newData);
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 2000);
        } catch {
          setSaveStatus("error");
        }
      }, autoSaveDebounceMs);
    },
    [autoSave, autoSaveDebounceMs]
  );

  // Leaving a step (Back, a sidebar click) or closing the form sends the
  // pending autosave right away instead of dropping the last edits.
  useEffect(() => {
    return () => {
      const pending = pendingAutoSave.current;
      cancelPendingAutoSave();
      if (pending) {
        onSaveRef.current(pending.stepId, pending.data).catch(() => {});
      }
    };
  }, [currentStepIndex]);

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      setData((prev) => ({ ...prev, [field]: value }));
      // The autosave payload comes from the latest data, not from inside the
      // state updater: React runs that updater later whenever an update is
      // already queued, and the autosave was then silently skipped.
      const nextData = { ...latestData.current, [field]: value };
      latestData.current = nextData;
      triggerAutoSave(currentStep.id, nextData);
      // Clear error for this field when user edits
      setErrors((prev) => {
        if (prev[field]) {
          const next = { ...prev };
          delete next[field];
          return next;
        }
        return prev;
      });
    },
    [currentStep?.id, triggerAutoSave]
  );

  const updateData = useCallback(
    (updater: (prev: Record<string, any>) => Record<string, any>) => {
      setData((prev) => updater(prev));
    },
    []
  );

  const getStepStatus = (index: number): FunnelStepStatus => {
    if (completedSteps.has(steps[index].id)) return "completed";
    if (index === currentStepIndex) return "in_progress";
    return "not_started";
  };

  const handleNext = async () => {
    if (isBusy || actionLockRef.current) return;

    setSubmitError(null);

    // Validate current step
    const stepErrors = validateStepFields(currentStep.fields, data);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    actionLockRef.current = true;
    setIsBusy(true);
    // This save carries the latest data, the pending autosave is not needed.
    cancelPendingAutoSave();

    try {
      // Save current step
      try {
        setSaveStatus("saving");
        await onSaveRef.current(currentStep.id, data);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } catch {
        setSaveStatus("error");
        return;
      }

      setCompletedSteps((prev) => new Set(prev).add(currentStep.id));

      if (isLastStep) {
        try {
          await onSubmitRef.current(data);
          submittedRef.current = true;
          // Edits typed while the submit was running are not autosaved.
          cancelPendingAutoSave();
        } catch (e: any) {
          setSubmitError({ key: e?.message || "error", values: e?.values });
        }
      } else {
        setCurrentStepIndex((prev) => prev + 1);
      }
    } finally {
      actionLockRef.current = false;
      setIsBusy(false);
    }
  };

  const handleSkip = async () => {
    if (!currentStep.skippable || isBusy || actionLockRef.current) return;
    setCompletedSteps((prev) => new Set(prev).add(currentStep.id));
    if (!isLastStep) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  };

  const handleStepClick = (index: number) => {
    // Allow navigating to completed or current steps
    if (index <= currentStepIndex || completedSteps.has(steps[index].id)) {
      setCurrentStepIndex(index);
      setErrors({});
    }
  };

  const saveStatusLabel =
    saveStatus === "saving"
      ? t("saving")
      : saveStatus === "saved"
        ? t("saved")
        : saveStatus === "error"
          ? t("save_error")
          : null;

  return (
    <div className="flex gap-6 min-h-[400px] flex-col md:flex-row">
      {!isSingleStep && (
        <div className="w-full md:w-60 shrink-0 md:border-r border-b md:border-b-0 border-gray-200 pb-3 md:pb-0 md:pr-4 pr-0 overflow-x-auto md:overflow-x-visible">
          <FunnelSidebar
            steps={steps}
            currentStepIndex={currentStepIndex}
            getStepStatus={getStepStatus}
            onStepClick={handleStepClick}
            lang={lang}
          />
        </div>
      )}
      <div className="flex-1 min-w-0 relative">
        {submitError && (
          <div className="mb-4">
            <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
              {t(submitError.key, submitError.values)}
            </div>
          </div>
        )}

        <div className="mb-5">
          <h2 className="text-lg font-semibold">
            {currentStep.title[lang] || currentStep.title.en}
          </h2>
          {/* On a phone this line stays in the flow under the title (reserved even when
              empty, so the form does not jump on every autosave); from md up it sits in
              the corner, where a long title cannot reach it. */}
          <p
            aria-live="polite"
            className="mt-1 min-h-5 text-sm text-gray-500 md:absolute md:top-0 md:right-0 md:mt-0 md:min-h-0"
          >
            {saveStatusLabel}
          </p>
          {currentStep.description && (
            <p className="mt-1 text-sm text-gray-600">
              {currentStep.description[lang] || currentStep.description.en}
            </p>
          )}
        </div>

        {currentStep.customComponent ? (
          <currentStep.customComponent
            data={data}
            onChange={handleFieldChange}
            errors={errors}
            updateData={updateData}
          />
        ) : (
          <FunnelStepRenderer
            fields={currentStep.fields}
            data={data}
            onChange={handleFieldChange}
            errors={errors}
            lang={lang}
          />
        )}

        {/* On a phone the buttons stay at the bottom edge of whatever scrolls (the modal body or the
            page's main area) instead of after the last field. The block is still in the flow, so the
            last field scrolls fully above it. */}
        <div data-testid="funnel-footer" className="mt-6 pt-4 border-t border-gray-200 max-md:sticky max-md:bottom-0 max-md:z-10 max-md:bg-white max-md:pb-3">
          <div className="flex flex-wrap items-center gap-3">
            {currentStepIndex > 0 && (
              <button
                type="button"
                className={clsx(
                  "px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50",
                  isTouch && "min-h-11"
                )}
                disabled={isBusy}
                onClick={() => {
                  setCurrentStepIndex((prev) => prev - 1);
                  setErrors({});
                }}
              >
                {t("back")}
              </button>
            )}
            {currentStep.skippable && (
              <button
                type="button"
                className={clsx(
                  "text-blue-600 hover:underline text-sm font-medium disabled:opacity-50",
                  isTouch && "min-h-11 px-3"
                )}
                disabled={isBusy}
                onClick={handleSkip}
              >
                {t("skip")}
              </button>
            )}
            <Button
              variant="primary"
              onClick={handleNext}
              loading={isBusy}
              disabled={isBusy}
            >
              {isLastStep ? config.submitLabel || t("submit") : t("next")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormFunnel;
